<template>
  <CollapsibleSection
    title="Solicitação automática de Registro de Atividade"
    test-id="policy-activity-request"
  >
    <template #title-prefix>
      <span
        v-if="readonly"
        class="mr-2 rounded-full border border-negative/40 bg-negative/10 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-negative"
        data-testid="policy-section-activity-request-lock"
      >
        🔒 leitura
      </span>
    </template>

    <!--
      `inert` (e não `pointer-events-none`) pelo mesmo motivo da seção "Auditoria": o
      requisito da Q31 é campo NÃO FOCÁVEL, e `pointer-events` deixaria tudo alcançável
      por Tab. `undefined` remove o atributo — `inert="false"` ainda é inerte.
    -->
    <div
      class="flex flex-col gap-5 text-left"
      data-testid="policy-activity-request-fields"
      :inert="readonly || undefined"
    >
      <p class="text-xs text-neutral50 m-0" data-testid="policy-activity-request-intro">
        Quando a perna (check-in ou check-out) fica sem registro, o sistema abre sozinho uma
        solicitação na fila unificada. Prazo por canal:
      </p>

      <!--
        I6/§6.3 — "só escalas com `policy.activityRecordsEnabled = true` entram". A seção fica
        visível com o toggle desligado (por ruling), mas o texto de abertura promete uma
        cobrança automática que nesse estado NÃO acontece: sem esta linha a Operação configura
        os prazos, salva e espera solicitações que nunca nascem. Os campos continuam
        editáveis — o aviso explica, não trava.
      -->
      <p
        v-if="activityRecordsEnabled === false"
        class="m-0 rounded-md border border-alert bg-[#FDF1DF] px-3 py-2 text-xs font-bold text-alert"
        data-testid="policy-activity-request-disabled"
      >
        Registro de Atividade desligado nesta escala: nenhuma solicitação é aberta enquanto o toggle
        acima estiver desligado.
      </p>

      <div
        v-for="row in channelRows"
        :key="row.channel"
        class="flex flex-col gap-1"
        :class="row.editable ? '' : 'opacity-50'"
        :data-testid="`policy-activity-request-row-${row.channel}`"
        @focusout="onRowFocusOut(row, $event)"
      >
        <div class="w-full sm:w-[420px]">
          <div :data-testid="`policy-activity-request-${row.channel}-input`">
            <v-text-field
              :model-value="row.displayValue"
              :label="row.label"
              :placeholder="String(row.fallback)"
              :suffix="row.unit"
              :disabled="readonly || !row.editable"
              :error-messages="errors[row.field]"
              type="number"
              min="0"
              :max="row.max"
              variant="outlined"
              density="comfortable"
              color="primary20"
              persistent-placeholder
              @update:model-value="onDelayInput(row, $event)"
            />
          </div>
        </div>

        <p class="text-xs text-neutral50 m-0">{{ row.help }}</p>

        <!--
          `0` não é "campo vazio": é a decisão explícita de não cobrar por este canal.
          A frase existe para que ninguém leia o zero como "ainda não configurado".
        -->
        <p
          v-if="isActivityRequestChannelOff(row.value)"
          class="text-xs font-bold text-neutral50 m-0"
          :data-testid="`policy-activity-request-${row.channel}-off`"
        >
          Sem solicitação automática por este canal.
        </p>

        <!--
          M4-2: o canal que não é o da modalidade continua visível — esconder faria o
          usuário procurar um prazo que existe no banco —, mas não é editável, porque
          configurar um prazo que o job jamais vai ler é configuração morta.
        -->
        <p
          v-if="!row.editable"
          class="text-xs text-neutral50 m-0"
          :data-testid="`policy-activity-request-${row.channel}-inactive`"
        >
          Não é a modalidade desta política: nenhuma solicitação é aberta por este canal.
        </p>

        <!--
          `role="alertdialog"` + `aria-live="assertive"`: a confirmação aparece sozinha, no meio
          do formulário, e decide entre "usar o default" e "não cobrar mais neste canal". Sem
          isto o leitor de tela não anuncia nada e o usuário sai da linha sem saber que havia
          uma pergunta em aberto.
        -->
        <div
          v-if="pendingEmptyChannel === row.channel"
          class="flex flex-wrap items-center gap-2 rounded-lg border border-attention/40 bg-attention/5 px-3 py-2"
          :data-testid="`policy-activity-request-${row.channel}-empty-confirm`"
          role="alertdialog"
          aria-live="assertive"
        >
          <span class="text-xs text-neutral50">
            Não cobrar neste canal? O prazo em branco não desliga a cobrança sozinho — confirme para
            gravar 0 e parar de abrir solicitações por “{{ row.label }}”.
          </span>
          <button
            type="button"
            class="rounded-md bg-primary30 px-3 py-1 text-xs font-bold text-white"
            :data-testid="`policy-activity-request-${row.channel}-empty-accept`"
            @click="confirmChannelOff(row)"
          >
            Não cobrar
          </button>
          <button
            type="button"
            class="rounded-md border border-neutral70 px-3 py-1 text-xs font-bold text-neutral50"
            :data-testid="`policy-activity-request-${row.channel}-empty-cancel`"
            @click="cancelChannelOff()"
          >
            Cancelar
          </button>
        </div>
      </div>

      <!--
        Q18 (spec 2026-09-25 §2.4) — janela de envio desde a abertura: horas corridas no app,
        dias corridos na folha.
      -->
      <div class="flex flex-col gap-2" data-testid="policy-send-window">
        <p class="m-0 text-sm font-bold text-neutral50">Prazo para enviar</p>
        <p class="m-0 text-xs text-neutral50" data-testid="policy-send-window-help">
          Conta a partir da abertura da solicitação, em {{ sendWindowUnitText }}. Vazio usa o
          padrão.
        </p>
        <div
          v-if="showAppWindow"
          class="w-full sm:w-[420px]"
          data-testid="policy-send-window-app-input"
        >
          <v-text-field
            :model-value="sendWindowHoursApp ?? ''"
            label="Prazo para enviar — App (horas)"
            :placeholder="APP_SEND_WINDOW_PLACEHOLDER"
            suffix="horas"
            type="number"
            min="1"
            max="720"
            :disabled="readonly"
            :error-messages="errors.activityRequestSendWindowHoursApp"
            variant="outlined"
            density="comfortable"
            color="primary20"
            persistent-placeholder
            @update:model-value="onAppWindowInput"
          />
        </div>
        <div
          v-if="showSheetWindow"
          class="w-full sm:w-[420px]"
          data-testid="policy-send-window-sheet-input"
        >
          <v-text-field
            :model-value="sendWindowDaysSheet ?? ''"
            label="Prazo para enviar — Folha (dias)"
            :placeholder="sheetWindowPlaceholder"
            suffix="dias"
            type="number"
            min="1"
            max="30"
            :disabled="readonly"
            :error-messages="errors.activityRequestSendWindowDaysSheet"
            variant="outlined"
            density="comfortable"
            color="primary20"
            persistent-placeholder
            @update:model-value="onSheetWindowInput"
          />
          <p class="m-0 text-xs text-neutral50" data-testid="policy-send-window-sheet-max">
            máximo 30 dias
          </p>
        </div>
      </div>

      <!--
        Balão ④ do M4. Fixo por decisão de produto (Q19): sem o cancelamento cruzado a
        fila acumularia cobrança de perna já coberta. Por isso é TEXTO, não um switch —
        não existe estado a salvar aqui.
      -->
      <div
        class="rounded-lg border border-neutral80 bg-neutral95/40 px-3 py-2.5"
        data-testid="policy-activity-request-cross-cancel"
      >
        <p class="m-0 text-sm font-bold text-neutral50">
          Cancelar automaticamente quando outro canal cobrir a perna
          <span class="ml-1 text-[10px] font-extrabold uppercase tracking-wide text-neutral60">
            fixo — sempre ligado
          </span>
        </p>
        <p class="m-0 mt-1 text-xs text-neutral50">
          Folha aprovada, check tardio do app, importação do App Externo ou envio manual da mesma
          perna encerram a solicitação (status Excluído, com motivo e evento na trilha). A perna
          coberta some da fila; a outra continua aberta.
        </p>
      </div>

      <p class="m-0 text-xs text-neutral50" data-testid="policy-activity-request-no-notification">
        🔔 Sem notificação push nesta fase. A cobrança é o próprio container na fila de auditoria
        (“Aguardando envio”) — nada é disparado para o médico no app.
      </p>
    </div>
  </CollapsibleSection>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import CollapsibleSection from '@/modules/company/Dashboard/components/CollapsibleSection.vue'
import {
  ActivityRequestChannel,
  ACTIVITY_REQUEST_CHANNEL_LABELS,
  ACTIVITY_REQUEST_DEFAULTS,
  ACTIVITY_REQUEST_MAX_DAYS,
  ACTIVITY_REQUEST_MAX_HOURS,
  DSAttendanceMonitoringType,
  DSAttendanceRuleRecurrence,
  isActivityRequestChannelOff
} from '@/constants/ds-attendance.constants'

import {
  APP_SEND_WINDOW_PLACEHOLDER,
  sheetSendWindowPlaceholder
} from './activityRequestSendWindow'

/** Campo do payload que cada canal escreve (§1.5). */
type DelayField =
  | 'requestAfterCheckoutHoursApp'
  | 'requestAfterDaysSheet'
  | 'requestAfterHoursExternal'

const props = withDefaults(
  defineProps<{
    monitoringType: DSAttendanceMonitoringType
    /** `null` = usar o default do produto; `0` = canal desligado. Nunca `undefined`. */
    requestAfterCheckoutHoursApp?: number | null
    requestAfterDaysSheet?: number | null
    requestAfterHoursExternal?: number | null
    /**
     * A política exige check do app (`requiredCheckOurTeam` ou `requiredCheckNotOurTeam`).
     * Só muda alguma coisa na modalidade folha — ver `editableChannels`.
     */
    requiresAppCheck?: boolean
    /**
     * O toggle "Aceitar Registro de Atividade" da escala. `false` mostra o aviso do I6: a
     * cobrança automática só roda com ele ligado (§6.3). Default `true` para não acusar
     * escala nenhuma quando o formulário ainda não passou o valor.
     */
    activityRecordsEnabled?: boolean
    /** Cadeado da Q31, calculado pelo formulário que monta esta seção. */
    readonly?: boolean
    /** Erros do `step3Schema`, indexados pelo nome do campo. */
    errors?: Record<string, string>
    /** Q18 — janela de envio (horas) de App Minha Escala e App Externo. `null` = 48 h. */
    sendWindowHoursApp?: number | null
    /** Q18 — janela de envio da folha (dias). `null` = derivada da recorrência. */
    sendWindowDaysSheet?: number | null
    /** Recorrências das regras da política, para o placeholder da folha. */
    sheetRecurrences?: DSAttendanceRuleRecurrence[]
  }>(),
  {
    requestAfterCheckoutHoursApp: null,
    requestAfterDaysSheet: null,
    requestAfterHoursExternal: null,
    requiresAppCheck: false,
    activityRecordsEnabled: true,
    readonly: false,
    errors: () => ({}),
    sendWindowHoursApp: null,
    sendWindowDaysSheet: null,
    sheetRecurrences: () => []
  }
)

const emit = defineEmits<{
  (e: 'update:requestAfterCheckoutHoursApp', value: number | null): void
  (e: 'update:requestAfterDaysSheet', value: number | null): void
  (e: 'update:requestAfterHoursExternal', value: number | null): void
  (e: 'update:sendWindowHoursApp', value: number | null): void
  (e: 'update:sendWindowDaysSheet', value: number | null): void
}>()

const showAppWindow = computed(
  () =>
    props.monitoringType === DSAttendanceMonitoringType.MINHA_ESCALA_APP ||
    props.monitoringType === DSAttendanceMonitoringType.EXTERNAL_APP
)
const showSheetWindow = computed(
  () =>
    props.monitoringType === DSAttendanceMonitoringType.FOLHA ||
    props.monitoringType === DSAttendanceMonitoringType.NON_DAILY_SHEET
)
/** App e folha são exclusivos: o campo visível decide a unidade do texto de ajuda. */
const sendWindowUnitText = computed(() =>
  showSheetWindow.value ? 'dias corridos' : 'horas corridas'
)
const sheetWindowPlaceholder = computed(() =>
  sheetSendWindowPlaceholder(props.monitoringType, props.sheetRecurrences)
)

/** Vazio = `null` (padrão). Sem confirmação: aqui não existe o "0 = desligar" dos prazos. */
const parseWindow = (raw: unknown): number | null => {
  const text = String(raw ?? '').trim()
  return text === '' ? null : Number(text)
}
const onAppWindowInput = (raw: unknown) => emit('update:sendWindowHoursApp', parseWindow(raw))
const onSheetWindowInput = (raw: unknown) => emit('update:sendWindowDaysSheet', parseWindow(raw))

/**
 * M4-2: o canal cobrado é o da modalidade; App Minha Escala entra JUNTO quando a
 * modalidade é folha E a política exige check do app, porque nesse caso o job usa o
 * MENOR dos dois prazos (§6.2) e esconder o campo deixaria o usuário sem explicação
 * para uma cobrança que chega em 2 h numa escala de folha.
 */
const editableChannels = computed<ActivityRequestChannel[]>(() => {
  switch (props.monitoringType) {
    case DSAttendanceMonitoringType.MINHA_ESCALA_APP:
      return [ActivityRequestChannel.MINHA_ESCALA_APP]
    case DSAttendanceMonitoringType.EXTERNAL_APP:
      return [ActivityRequestChannel.EXTERNAL_APP]
    case DSAttendanceMonitoringType.FOLHA:
    case DSAttendanceMonitoringType.NON_DAILY_SHEET:
      return props.requiresAppCheck
        ? [ActivityRequestChannel.FOLHA, ActivityRequestChannel.MINHA_ESCALA_APP]
        : [ActivityRequestChannel.FOLHA]
    default:
      return []
  }
})

interface ChannelRow {
  channel: ActivityRequestChannel
  field: DelayField
  label: string
  unit: string
  help: string
  /** Valor do placeholder quando o prazo é `null` — o default do produto (Q9). */
  fallback: number
  max: number
  value: number | null
  displayValue: string
  editable: boolean
}

/** Canal cujo campo acabou de ficar vazio e espera a confirmação "não cobrar". */
const pendingEmptyChannel = ref<ActivityRequestChannel | null>(null)

const CHANNEL_SPECS = [
  {
    channel: ActivityRequestChannel.MINHA_ESCALA_APP,
    field: 'requestAfterCheckoutHoursApp' as DelayField,
    unit: 'horas',
    help: 'Após o check-out previsto, sem check-out registrado.',
    fallback: ACTIVITY_REQUEST_DEFAULTS.afterCheckoutHoursApp,
    max: ACTIVITY_REQUEST_MAX_HOURS
  },
  {
    channel: ActivityRequestChannel.FOLHA,
    field: 'requestAfterDaysSheet' as DelayField,
    unit: 'dias',
    help: 'Após o plantão, sem vínculo a uma folha.',
    fallback: ACTIVITY_REQUEST_DEFAULTS.afterDaysSheet,
    max: ACTIVITY_REQUEST_MAX_DAYS
  },
  {
    channel: ActivityRequestChannel.EXTERNAL_APP,
    field: 'requestAfterHoursExternal' as DelayField,
    unit: 'horas',
    help: 'Após o check-out previsto, sem registro importado.',
    fallback: ACTIVITY_REQUEST_DEFAULTS.afterHoursExternal,
    max: ACTIVITY_REQUEST_MAX_HOURS
  }
] as const

const valueOf = (field: DelayField): number | null => props[field] ?? null

const channelRows = computed<ChannelRow[]>(() =>
  CHANNEL_SPECS.map((spec) => {
    const value = valueOf(spec.field)
    return {
      ...spec,
      label: ACTIVITY_REQUEST_CHANNEL_LABELS[spec.channel],
      value,
      // Campo em branco enquanto a confirmação está pendente: o usuário apagou e ainda
      // não disse o que isso significa.
      displayValue:
        pendingEmptyChannel.value === spec.channel || value === null ? '' : String(value),
      editable: editableChannels.value.includes(spec.channel)
    }
  })
)

const emitDelay = (field: DelayField, value: number | null) => {
  if (field === 'requestAfterCheckoutHoursApp') emit('update:requestAfterCheckoutHoursApp', value)
  else if (field === 'requestAfterDaysSheet') emit('update:requestAfterDaysSheet', value)
  else emit('update:requestAfterHoursExternal', value)
}

/**
 * Esvaziar o campo NÃO grava nada sozinho. `null` ("usar o default do produto") e `0`
 * ("não cobrar neste canal") são estados diferentes e indistinguíveis a partir de um
 * campo em branco — por isso a confirmação explícita do M4 ③.
 */
const onDelayInput = (row: ChannelRow, raw: unknown) => {
  const text = String(raw ?? '').trim()

  if (text === '') {
    pendingEmptyChannel.value = row.channel
    return
  }

  pendingEmptyChannel.value = null
  const parsed = Number(text)
  emitDelay(row.field, Number.isNaN(parsed) ? null : parsed)
}

const confirmChannelOff = (row: ChannelRow) => {
  pendingEmptyChannel.value = null
  emitDelay(row.field, 0)
}

const cancelChannelOff = () => {
  pendingEmptyChannel.value = null
}

/**
 * Sair da linha sem responder equivale a desistir: o campo volta a mostrar o valor que
 * continua no payload. Antes o input ficava em branco enquanto o prazo antigo seguia gravado,
 * e o usuário salvava "em branco" e via o número voltar.
 *
 * `focusout` na LINHA (e não `blur` no input) porque clicar em "Não cobrar" tira o foco do
 * campo: o `relatedTarget` dentro da própria linha mantém a pergunta de pé.
 */
const onRowFocusOut = (row: ChannelRow, event: FocusEvent) => {
  if (pendingEmptyChannel.value !== row.channel) return

  const next = event.relatedTarget as Node | null
  const container = event.currentTarget as HTMLElement | null
  if (next && container?.contains(next)) return

  pendingEmptyChannel.value = null
}
</script>
